import {
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    FormLayout,
    InlineStack,
    Layout,
    Page,
    PageActions,
    Select,
    SkeletonBodyText,
    SkeletonPage,
    Tabs,
    Text,
    TextField,
} from '@shopify/polaris';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import agent from '../../app/api/agent';
import { RealtimeProduct } from '../../app/models/Product';
import {
    createDefaultProductGuide,
    ProductGuideAnswer,
    ProductGuideAnswerRule,
    ProductGuideCollectionOption,
    ProductGuideDetail,
    ProductGuideQuestion,
    ProductGuideResultGroup,
    ProductGuideResultGroupFilterScope,
    ProductGuideResultProduct,
    ProductGuideSelectionMode,
} from '../../app/models/ProductGuide';
import { useStore } from '../../app/stores/store';

const selectionModeOptions = [
    { label: 'Single choice', value: 'SingleChoice' },
    { label: 'Multiple choice', value: 'MultipleChoice' },
];

const answerRuleEffectOptions = [
    { label: 'Require matches', value: 'Require' },
    { label: 'Exclude matches', value: 'Exclude' },
    { label: 'Prefer matches', value: 'Prefer' },
];

const noResultsBehaviorOptions = [
    { label: 'Show empty message', value: 'ShowEmpty' },
    { label: 'Hide this slot', value: 'HideGroup' },
    { label: 'Use fallback collection', value: 'UseFallbackCollection' },
];

const tabs = [
    { id: 'setup', content: 'Setup', accessibilityLabel: 'Guide setup', panelID: 'setup-panel' },
    { id: 'questions', content: 'Questions', accessibilityLabel: 'Shopper questions', panelID: 'questions-panel' },
    { id: 'slots', content: 'Package slots', accessibilityLabel: 'Package slots', panelID: 'slots-panel' },
    { id: 'preview', content: 'Preview', accessibilityLabel: 'Guide preview', panelID: 'preview-panel' },
];

// Rewrites displayOrder after add/remove operations so the server receives stable ordering.
const toOrdered = <T extends { displayOrder: number }>(items: T[]) =>
    items.map((item, index) => ({ ...item, displayOrder: index + 1 }));

// Converts labels to stable keys when the merchant leaves technical keys blank.
const toKey = (value: string, fallback: string) => {
    const cleaned = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return cleaned || fallback;
};

const parseWholeNumber = (value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

const parseNullableWholeNumber = (value: string) => {
    const parsed = parseWholeNumber(value);
    return parsed > 0 ? parsed : null;
};

const numberValue = (value?: number | null) => (value && value > 0 ? String(value) : '');

const createQuestion = (displayOrder: number): ProductGuideQuestion => ({
    prompt: '',
    helpText: '',
    selectionMode: 'SingleChoice',
    isRequired: true,
    displayOrder,
    answers: [],
});

const createAnswer = (displayOrder: number): ProductGuideAnswer => ({
    label: '',
    valueKey: '',
    helpText: '',
    isDefault: false,
    displayOrder,
    rules: [],
});

const createAnswerRule = (displayOrder: number): ProductGuideAnswerRule => ({
    source: 'ShopifyCollection',
    effect: 'Require',
    shopifyCollectionId: null,
    shopifyCollectionTitle: '',
    shopifyCollectionHandle: '',
    isActive: true,
    displayOrder,
});

const createResultGroup = (displayOrder: number): ProductGuideResultGroup => ({
    title: '',
    slotKey: '',
    description: '',
    isRequired: true,
    minSelections: 1,
    maxSelections: 1,
    baseShopifyCollectionId: null,
    baseShopifyCollectionTitle: '',
    baseShopifyCollectionHandle: '',
    noResultsBehavior: 'ShowEmpty',
    fallbackShopifyCollectionId: null,
    fallbackShopifyCollectionTitle: '',
    fallbackShopifyCollectionHandle: '',
    displayOrder,
    filterScopes: [],
    products: [],
});

const createCollectionFilterScope = (collection: ProductGuideCollectionOption): ProductGuideResultGroupFilterScope => ({
    scopeType: 'ShopifyCollection',
    shopifyCollectionId: collection.id,
    shopifyCollectionTitle: collection.title,
    shopifyCollectionHandle: collection.handle,
    isActive: true,
});

const createResultProduct = (displayOrder: number): ProductGuideResultProduct => ({
    shopifyProductId: 0,
    shopifyProductVariantId: null,
    productTitle: '',
    variantTitle: '',
    sku: '',
    featuredImage: '',
    quantity: 1,
    isDefaultSelected: false,
    displayOrder,
});

const ProductGuideEditorPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { productGuideStore } = useStore();
    const {
        loadingInitial,
        loadingSave,
        selectedProductGuide,
        loadProductGuide,
        saveProductGuide,
        deleteProductGuide,
        clearSelectedProductGuide,
    } = productGuideStore;

    const guideId = useMemo(() => (id && id !== 'new' ? Number(id) : undefined), [id]);
    const [guide, setGuide] = useState<ProductGuideDetail | undefined>(undefined);
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number | null>(0);
    const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(0);
    const [showSetupAdvanced, setShowSetupAdvanced] = useState(false);
    const [showQuestionAdvanced, setShowQuestionAdvanced] = useState(false);
    const [showSlotAdvanced, setShowSlotAdvanced] = useState(false);
    const [saveError, setSaveError] = useState<string | undefined>(undefined);
    const [setupDataError, setSetupDataError] = useState<string | undefined>(undefined);
    const [collections, setCollections] = useState<ProductGuideCollectionOption[]>([]);
    const [productSearchTerms, setProductSearchTerms] = useState<Record<number, string>>({});
    const [productSearchResults, setProductSearchResults] = useState<Record<number, RealtimeProduct[]>>({});
    const [productSearchLoading, setProductSearchLoading] = useState<Record<number, boolean>>({});

    // Shopify collections are the guide's merchant-facing product set abstraction.
    useEffect(() => {
        let cancelled = false;

        const loadSetupData = async () => {
            try {
                const loadedCollections = await agent.ProductGuides.collections();

                if (!cancelled) {
                    setCollections(loadedCollections);
                    setSetupDataError(undefined);
                }
            } catch {
                if (!cancelled) {
                    setSetupDataError('Shopify collections could not be loaded.');
                }
            }
        };

        loadSetupData();
        return () => {
            cancelled = true;
        };
    }, []);

    // Load an existing guide or seed a new one; API calls still enforce shop access.
    useEffect(() => {
        if (guideId) {
            loadProductGuide(guideId);
        } else {
            clearSelectedProductGuide();
            setGuide(createDefaultProductGuide());
            setSelectedQuestionIndex(0);
            setSelectedGroupIndex(0);
        }

        return () => clearSelectedProductGuide();
    }, [clearSelectedProductGuide, guideId, loadProductGuide]);

    // Copy the store-loaded guide into local editable state once detail loading finishes.
    useEffect(() => {
        if (guideId && selectedProductGuide) {
            setGuide(selectedProductGuide);
            setSelectedQuestionIndex(selectedProductGuide.questions.length > 0 ? 0 : null);
            setSelectedGroupIndex(selectedProductGuide.resultGroups.length > 0 ? 0 : null);
        }
    }, [guideId, selectedProductGuide]);

    const activeQuestionIndex =
        guide && selectedQuestionIndex !== null && guide.questions[selectedQuestionIndex]
            ? selectedQuestionIndex
            : guide && guide.questions.length > 0
                ? 0
                : null;

    const activeGroupIndex =
        guide && selectedGroupIndex !== null && guide.resultGroups[selectedGroupIndex]
            ? selectedGroupIndex
            : guide && guide.resultGroups.length > 0
                ? 0
                : null;

    const activeQuestion = activeQuestionIndex !== null && guide
        ? guide.questions[activeQuestionIndex]
        : undefined;

    const activeGroup = activeGroupIndex !== null && guide
        ? guide.resultGroups[activeGroupIndex]
        : undefined;

    const collectionOptions = useMemo(() => [
        { label: 'Select collection', value: '' },
        ...collections.map((collection) => ({
            label: collection.collectionType ? `${collection.title} (${collection.collectionType})` : collection.title,
            value: String(collection.id),
        })),
    ], [collections]);

    const answerFilterCollections = useMemo(() => {
        if (!guide) return [];

        const usedIds = new Set<number>();
        return guide.questions
            .flatMap((question) => question.answers)
            .flatMap((answer) => answer.rules)
            .filter((rule) => rule.shopifyCollectionId)
            .map((rule) => collections.find((collection) => collection.id === rule.shopifyCollectionId))
            .filter((collection): collection is ProductGuideCollectionOption => {
                if (!collection || usedIds.has(collection.id)) {
                    return false;
                }

                usedIds.add(collection.id);
                return true;
            });
    }, [collections, guide]);

    const findCollection = useCallback((id?: number | null) =>
        collections.find((collection) => collection.id === id),
    [collections]);

    // Patch top-level guide fields without replacing child collections.
    const patchGuide = useCallback((changes: Partial<ProductGuideDetail>) => {
        setGuide((current) => current ? { ...current, ...changes } : current);
    }, []);

    const patchQuestion = useCallback((questionIndex: number, changes: Partial<ProductGuideQuestion>) => {
        setGuide((current) => current ? {
            ...current,
            questions: current.questions.map((question, index) =>
                index === questionIndex ? { ...question, ...changes } : question
            ),
        } : current);
    }, []);

    const patchAnswer = useCallback((questionIndex: number, answerIndex: number, changes: Partial<ProductGuideAnswer>) => {
        setGuide((current) => current ? {
            ...current,
            questions: current.questions.map((question, qIndex) =>
                qIndex === questionIndex
                    ? {
                        ...question,
                        answers: question.answers.map((answer, aIndex) =>
                            aIndex === answerIndex ? { ...answer, ...changes } : answer
                        ),
                    }
                    : question
            ),
        } : current);
    }, []);

    const patchAnswerRule = useCallback((questionIndex: number, answerIndex: number, ruleIndex: number, changes: Partial<ProductGuideAnswerRule>) => {
        setGuide((current) => current ? {
            ...current,
            questions: current.questions.map((question, qIndex) =>
                qIndex === questionIndex
                    ? {
                        ...question,
                        answers: question.answers.map((answer, aIndex) =>
                            aIndex === answerIndex
                                ? {
                                    ...answer,
                                    rules: answer.rules.map((rule, rIndex) =>
                                        rIndex === ruleIndex ? { ...rule, ...changes } : rule
                                    ),
                                }
                                : answer
                        ),
                    }
                    : question
            ),
        } : current);
    }, []);

    const patchResultGroup = useCallback((groupIndex: number, changes: Partial<ProductGuideResultGroup>) => {
        setGuide((current) => current ? {
            ...current,
            resultGroups: current.resultGroups.map((group, index) =>
                index === groupIndex ? { ...group, ...changes } : group
            ),
        } : current);
    }, []);

    const patchResultProduct = useCallback((groupIndex: number, productIndex: number, changes: Partial<ProductGuideResultProduct>) => {
        setGuide((current) => current ? {
            ...current,
            resultGroups: current.resultGroups.map((group, gIndex) =>
                gIndex === groupIndex
                    ? {
                        ...group,
                        products: group.products.map((product, pIndex) =>
                            pIndex === productIndex ? { ...product, ...changes } : product
                        ),
                    }
                    : group
            ),
        } : current);
    }, []);

    const addQuestion = useCallback(() => {
        if (!guide) return;
        const nextIndex = guide.questions.length;
        setGuide((current) => current ? {
            ...current,
            questions: toOrdered([...current.questions, createQuestion(current.questions.length + 1)]),
        } : current);
        setSelectedQuestionIndex(nextIndex);
    }, [guide]);

    const removeQuestion = useCallback((questionIndex: number) => {
        setGuide((current) => current ? {
            ...current,
            questions: toOrdered(current.questions.filter((_, index) => index !== questionIndex)),
        } : current);
        setSelectedQuestionIndex((current) => current === null || current <= 0 ? 0 : current - 1);
    }, []);

    const addAnswer = useCallback((questionIndex: number) => {
        setGuide((current) => current ? {
            ...current,
            questions: current.questions.map((question, index) =>
                index === questionIndex
                    ? { ...question, answers: toOrdered([...question.answers, createAnswer(question.answers.length + 1)]) }
                    : question
            ),
        } : current);
    }, []);

    const removeAnswer = useCallback((questionIndex: number, answerIndex: number) => {
        setGuide((current) => current ? {
            ...current,
            questions: current.questions.map((question, index) =>
                index === questionIndex
                    ? { ...question, answers: toOrdered(question.answers.filter((_, aIndex) => aIndex !== answerIndex)) }
                    : question
            ),
        } : current);
    }, []);

    const addAnswerRule = useCallback((questionIndex: number, answerIndex: number) => {
        setGuide((current) => current ? {
            ...current,
            questions: current.questions.map((question, qIndex) =>
                qIndex === questionIndex
                    ? {
                        ...question,
                        answers: question.answers.map((answer, aIndex) =>
                            aIndex === answerIndex
                                ? { ...answer, rules: toOrdered([...answer.rules, createAnswerRule(answer.rules.length + 1)]) }
                                : answer
                        ),
                    }
                    : question
            ),
        } : current);
        setShowQuestionAdvanced(true);
    }, []);

    const removeAnswerRule = useCallback((questionIndex: number, answerIndex: number, ruleIndex: number) => {
        setGuide((current) => current ? {
            ...current,
            questions: current.questions.map((question, qIndex) =>
                qIndex === questionIndex
                    ? {
                        ...question,
                        answers: question.answers.map((answer, aIndex) =>
                            aIndex === answerIndex
                                ? { ...answer, rules: toOrdered(answer.rules.filter((_, rIndex) => rIndex !== ruleIndex)) }
                                : answer
                        ),
                    }
                    : question
            ),
        } : current);
    }, []);

    const addResultGroup = useCallback(() => {
        if (!guide) return;
        const nextIndex = guide.resultGroups.length;
        setGuide((current) => current ? {
            ...current,
            resultGroups: toOrdered([...current.resultGroups, createResultGroup(current.resultGroups.length + 1)]),
        } : current);
        setSelectedGroupIndex(nextIndex);
    }, [guide]);

    const removeResultGroup = useCallback((groupIndex: number) => {
        setGuide((current) => current ? {
            ...current,
            resultGroups: toOrdered(current.resultGroups.filter((_, index) => index !== groupIndex)),
        } : current);
        setSelectedGroupIndex((current) => current === null || current <= 0 ? 0 : current - 1);
    }, []);

    const addResultProduct = useCallback((groupIndex: number) => {
        setGuide((current) => current ? {
            ...current,
            resultGroups: current.resultGroups.map((group, index) =>
                index === groupIndex
                    ? { ...group, products: toOrdered([...group.products, createResultProduct(group.products.length + 1)]) }
                    : group
            ),
        } : current);
        setShowSlotAdvanced(true);
    }, []);

    const addSearchedProduct = useCallback((groupIndex: number, product: RealtimeProduct) => {
        setGuide((current) => current ? {
            ...current,
            resultGroups: current.resultGroups.map((group, index) =>
                index === groupIndex
                    ? {
                        ...group,
                        products: toOrdered([
                            ...group.products,
                            {
                                ...createResultProduct(group.products.length + 1),
                                shopifyProductId: product.id,
                                productTitle: product.title,
                                sku: product.sku,
                                featuredImage: product.featuredImage,
                            },
                        ]),
                    }
                    : group
            ),
        } : current);
    }, []);

    const removeResultProduct = useCallback((groupIndex: number, productIndex: number) => {
        setGuide((current) => current ? {
            ...current,
            resultGroups: current.resultGroups.map((group, index) =>
                index === groupIndex
                    ? { ...group, products: toOrdered(group.products.filter((_, pIndex) => pIndex !== productIndex)) }
                    : group
            ),
        } : current);
    }, []);

    const toggleCollectionScope = useCallback((groupIndex: number, collection: ProductGuideCollectionOption, checked: boolean) => {
        setGuide((current) => current ? {
            ...current,
            resultGroups: current.resultGroups.map((group, index) => {
                if (index !== groupIndex) return group;

                const existingScopes = group.filterScopes.filter((scope) =>
                    !(scope.scopeType === 'ShopifyCollection' && scope.shopifyCollectionId === collection.id)
                );

                return {
                    ...group,
                    filterScopes: checked
                        ? [...existingScopes, createCollectionFilterScope(collection)]
                        : existingScopes,
                };
            }),
        } : current);
    }, []);

    const searchProducts = async (groupIndex: number) => {
        const query = productSearchTerms[groupIndex]?.trim() ?? '';
        if (!query) {
            setProductSearchResults((current) => ({ ...current, [groupIndex]: [] }));
            return;
        }

        setProductSearchLoading((current) => ({ ...current, [groupIndex]: true }));
        try {
            const result = await agent.Products.listRealtime(query, '', 10, 'title asc');
            setProductSearchResults((current) => ({ ...current, [groupIndex]: result.products }));
        } finally {
            setProductSearchLoading((current) => ({ ...current, [groupIndex]: false }));
        }
    };

    const handleAnswerLabelChange = (questionIndex: number, answerIndex: number, value: string) => {
        const answer = guide?.questions[questionIndex]?.answers[answerIndex];
        patchAnswer(questionIndex, answerIndex, {
            label: value,
            valueKey: answer?.valueKey ? answer.valueKey : toKey(value, `answer-${answerIndex + 1}`),
        });
    };

    const handleGroupTitleChange = (groupIndex: number, value: string) => {
        const group = guide?.resultGroups[groupIndex];
        patchResultGroup(groupIndex, {
            title: value,
            slotKey: group?.slotKey ? group.slotKey : toKey(value, `slot-${groupIndex + 1}`),
        });
    };

    const handleSave = async () => {
        if (!guide) return;

        setSaveError(undefined);
        try {
            const saved = await saveProductGuide(guide);
            setGuide(saved);

            if (!guideId && saved.id) {
                navigate(`/product-guides/${saved.id}`, { replace: true });
            }
        } catch {
            setSaveError('Product guide could not be saved. Check required fields and collection selections.');
        }
    };

    const handleDelete = async () => {
        if (!guide?.id) return;

        const confirmed = window.confirm(`Delete ${guide.name}?`);
        if (!confirmed) return;

        await deleteProductGuide(guide.id);
        navigate('/product-guides');
    };

    if ((loadingInitial && guideId) || !guide) {
        return (
            <SkeletonPage title="Product Guide">
                <Layout>
                    <Layout.Section>
                        <Box padding="400">
                            <SkeletonBodyText />
                        </Box>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
        );
    }

    const setupTabMarkup = (
        <Layout>
            <Layout.Section>
                <Card>
                    <Box padding="500">
                        <BlockStack gap="500">
                            <Text as="h2" variant="headingMd">Guide setup</Text>

                            <FormLayout>
                                <TextField
                                    label="Name"
                                    value={guide.name}
                                    onChange={(value) => patchGuide({ name: value, handle: guide.handle || toKey(value, 'guide') })}
                                    autoComplete="off"
                                />
                                <TextField
                                    label="Description"
                                    value={guide.description}
                                    onChange={(value) => patchGuide({ description: value })}
                                    autoComplete="off"
                                    multiline={3}
                                />
                                <InlineStack gap="500" wrap>
                                    <Checkbox label="Active" checked={guide.isActive} onChange={(value) => patchGuide({ isActive: value })} />
                                    <Checkbox label="Requires vehicle" checked={guide.requiresVehicleContext} onChange={(value) => patchGuide({ requiresVehicleContext: value })} />
                                    <Checkbox label="Bundle builder" checked={guide.bundleBuilderEnabled} onChange={(value) => patchGuide({ bundleBuilderEnabled: value })} />
                                </InlineStack>
                            </FormLayout>

                            <Box>
                                <Button onClick={() => setShowSetupAdvanced((current) => !current)}>
                                    {showSetupAdvanced ? 'Hide advanced fields' : 'Show advanced fields'}
                                </Button>
                            </Box>

                            {showSetupAdvanced && (
                                <FormLayout>
                                    <TextField
                                        label="Handle"
                                        value={guide.handle}
                                        onChange={(value) => patchGuide({ handle: value })}
                                        autoComplete="off"
                                    />
                                    <TextField
                                        label="Guide type"
                                        value={guide.guideType}
                                        onChange={(value) => patchGuide({ guideType: value })}
                                        autoComplete="off"
                                    />
                                </FormLayout>
                            )}
                        </BlockStack>
                    </Box>
                </Card>
            </Layout.Section>

            <Layout.Section variant="oneThird">
                <Card>
                    <Box padding="500">
                        <BlockStack gap="400">
                            <InlineStack align="space-between" blockAlign="center">
                                <Text as="h2" variant="headingMd">Bundle discount</Text>
                                <Checkbox label="Enabled" checked={guide.discountEnabled} onChange={(value) => patchGuide({ discountEnabled: value })} />
                            </InlineStack>
                            {guide.discountEnabled ? (
                                <FormLayout>
                                    <TextField
                                        label="Minimum items"
                                        type="number"
                                        value={numberValue(guide.discountMinimumItems)}
                                        onChange={(value) => patchGuide({ discountMinimumItems: parseNullableWholeNumber(value) })}
                                        autoComplete="off"
                                    />
                                    <TextField
                                        label="Discount percentage"
                                        type="number"
                                        value={guide.discountPercentage !== null && guide.discountPercentage !== undefined ? String(guide.discountPercentage) : ''}
                                        onChange={(value) => patchGuide({ discountPercentage: value === '' ? null : Number(value) })}
                                        autoComplete="off"
                                        suffix="%"
                                    />
                                </FormLayout>
                            ) : (
                                <Text as="p" variant="bodyMd" tone="subdued">Off</Text>
                            )}
                        </BlockStack>
                    </Box>
                </Card>
            </Layout.Section>
        </Layout>
    );

    const questionsTabMarkup = (
        <Layout>
            <Layout.Section variant="oneThird">
                <Card>
                    <Box padding="500">
                        <BlockStack gap="400">
                            <InlineStack align="space-between" blockAlign="center">
                                <Text as="h2" variant="headingMd">Questions</Text>
                                <Button onClick={addQuestion}>Add</Button>
                            </InlineStack>

                            {guide.questions.length === 0 ? (
                                <Text as="p" tone="subdued">No questions yet.</Text>
                            ) : guide.questions.map((question, index) => (
                                <Box
                                    key={question.id ?? `question-${index}`}
                                    padding="300"
                                    borderWidth="025"
                                    borderColor={activeQuestionIndex === index ? 'border-emphasis' : 'border'}
                                    borderRadius="200"
                                >
                                    <InlineStack align="space-between" blockAlign="center" gap="300">
                                        <BlockStack gap="050">
                                            <Text as="span" variant="bodyMd" fontWeight="bold">
                                                {question.prompt || `Question ${index + 1}`}
                                            </Text>
                                            <Text as="span" variant="bodySm" tone="subdued">
                                                {question.answers.length} answer{question.answers.length === 1 ? '' : 's'} / {question.isRequired ? 'Required' : 'Optional'}
                                            </Text>
                                        </BlockStack>
                                        <Button onClick={() => setSelectedQuestionIndex(index)}>Edit</Button>
                                    </InlineStack>
                                </Box>
                            ))}
                        </BlockStack>
                    </Box>
                </Card>
            </Layout.Section>

            <Layout.Section>
                <Card>
                    <Box padding="500">
                        {activeQuestion && activeQuestionIndex !== null ? (
                            <BlockStack gap="500">
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h2" variant="headingMd">Edit question</Text>
                                    <Button tone="critical" onClick={() => removeQuestion(activeQuestionIndex)}>Remove</Button>
                                </InlineStack>

                                <FormLayout>
                                    <TextField
                                        label="Prompt"
                                        value={activeQuestion.prompt}
                                        onChange={(value) => patchQuestion(activeQuestionIndex, { prompt: value })}
                                        autoComplete="off"
                                    />
                                    <Select
                                        label="Selection mode"
                                        options={selectionModeOptions}
                                        value={activeQuestion.selectionMode}
                                        onChange={(value) => patchQuestion(activeQuestionIndex, { selectionMode: value as ProductGuideSelectionMode })}
                                    />
                                    <Checkbox
                                        label="Required"
                                        checked={activeQuestion.isRequired}
                                        onChange={(value) => patchQuestion(activeQuestionIndex, { isRequired: value })}
                                    />
                                </FormLayout>

                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h3" variant="headingSm">Answers</Text>
                                    <Button onClick={() => addAnswer(activeQuestionIndex)}>Add answer</Button>
                                </InlineStack>

                                <BlockStack gap="300">
                                    {activeQuestion.answers.length === 0 && (
                                        <Text as="p" tone="subdued">No answers yet.</Text>
                                    )}
                                    {activeQuestion.answers.map((answer, answerIndex) => (
                                        <Box key={answer.id ?? `answer-${answerIndex}`} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                                            <BlockStack gap="300">
                                                <InlineStack align="space-between" blockAlign="center">
                                                    <Text as="span" variant="bodyMd" fontWeight="bold">
                                                        Answer {answerIndex + 1}
                                                    </Text>
                                                    <Button tone="critical" onClick={() => removeAnswer(activeQuestionIndex, answerIndex)}>Remove</Button>
                                                </InlineStack>
                                                <FormLayout>
                                                    <TextField
                                                        label="Label"
                                                        value={answer.label}
                                                        onChange={(value) => handleAnswerLabelChange(activeQuestionIndex, answerIndex, value)}
                                                        autoComplete="off"
                                                    />
                                                    <Checkbox
                                                        label="Default answer"
                                                        checked={answer.isDefault}
                                                        onChange={(value) => patchAnswer(activeQuestionIndex, answerIndex, { isDefault: value })}
                                                    />
                                                </FormLayout>

                                                {showQuestionAdvanced && (
                                                    <BlockStack gap="300">
                                                        <FormLayout>
                                                            <TextField
                                                                label="Value key"
                                                                value={answer.valueKey}
                                                                onChange={(value) => patchAnswer(activeQuestionIndex, answerIndex, { valueKey: value })}
                                                                autoComplete="off"
                                                            />
                                                            <TextField
                                                                label="Answer help text"
                                                                value={answer.helpText}
                                                                onChange={(value) => patchAnswer(activeQuestionIndex, answerIndex, { helpText: value })}
                                                                autoComplete="off"
                                                            />
                                                        </FormLayout>

                                                        <Box padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                                                            <BlockStack gap="300">
                                                                <InlineStack align="space-between" blockAlign="center">
                                                                    <Text as="h4" variant="headingSm">Answer filters</Text>
                                                                    <Button onClick={() => addAnswerRule(activeQuestionIndex, answerIndex)}>Add filter</Button>
                                                                </InlineStack>

                                                                {answer.rules.length === 0 && (
                                                                    <Text as="p" tone="subdued">No filters are attached to this answer.</Text>
                                                                )}

                                                                {answer.rules.map((rule, ruleIndex) => (
                                                                    <Box key={rule.id ?? `answer-rule-${ruleIndex}`} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                                                                        <BlockStack gap="300">
                                                                            <InlineStack align="space-between" blockAlign="center">
                                                                                <Text as="span" fontWeight="bold">Filter {ruleIndex + 1}</Text>
                                                                                <Button tone="critical" onClick={() => removeAnswerRule(activeQuestionIndex, answerIndex, ruleIndex)}>Remove</Button>
                                                                            </InlineStack>
                                                                            <FormLayout>
                                                                                <Select
                                                                                    label="Collection"
                                                                                    options={collectionOptions}
                                                                                    value={rule.shopifyCollectionId ? String(rule.shopifyCollectionId) : ''}
                                                                                    onChange={(value) => {
                                                                                        const collection = findCollection(value ? Number(value) : null);
                                                                                        patchAnswerRule(activeQuestionIndex, answerIndex, ruleIndex, {
                                                                                            shopifyCollectionId: collection?.id ?? null,
                                                                                            shopifyCollectionTitle: collection?.title ?? '',
                                                                                            shopifyCollectionHandle: collection?.handle ?? '',
                                                                                        });
                                                                                    }}
                                                                                />
                                                                                <Select
                                                                                    label="Effect"
                                                                                    options={answerRuleEffectOptions}
                                                                                    value={rule.effect}
                                                                                    onChange={(value) => patchAnswerRule(activeQuestionIndex, answerIndex, ruleIndex, { effect: value as ProductGuideAnswerRule['effect'] })}
                                                                                />
                                                                                <Checkbox
                                                                                    label="Active"
                                                                                    checked={rule.isActive}
                                                                                    onChange={(value) => patchAnswerRule(activeQuestionIndex, answerIndex, ruleIndex, { isActive: value })}
                                                                                />
                                                                            </FormLayout>
                                                                        </BlockStack>
                                                                    </Box>
                                                                ))}
                                                            </BlockStack>
                                                        </Box>
                                                    </BlockStack>
                                                )}
                                            </BlockStack>
                                        </Box>
                                    ))}
                                </BlockStack>

                                <Box>
                                    <Button onClick={() => setShowQuestionAdvanced((current) => !current)}>
                                        {showQuestionAdvanced ? 'Hide filters and details' : 'Show filters and details'}
                                    </Button>
                                </Box>

                                {showQuestionAdvanced && (
                                    <TextField
                                        label="Question help text"
                                        value={activeQuestion.helpText}
                                        onChange={(value) => patchQuestion(activeQuestionIndex, { helpText: value })}
                                        autoComplete="off"
                                    />
                                )}
                            </BlockStack>
                        ) : (
                            <Text as="p" tone="subdued">Select or add a question.</Text>
                        )}
                    </Box>
                </Card>
            </Layout.Section>
        </Layout>
    );

    const slotsTabMarkup = (
        <Layout>
            <Layout.Section variant="oneThird">
                <Card>
                    <Box padding="500">
                        <BlockStack gap="400">
                            <InlineStack align="space-between" blockAlign="center">
                                <Text as="h2" variant="headingMd">Package slots</Text>
                                <Button onClick={addResultGroup}>Add</Button>
                            </InlineStack>

                            {guide.resultGroups.length === 0 ? (
                                <Text as="p" tone="subdued">No package slots yet.</Text>
                            ) : guide.resultGroups.map((group, index) => (
                                <Box
                                    key={group.id ?? `group-${index}`}
                                    padding="300"
                                    borderWidth="025"
                                    borderColor={activeGroupIndex === index ? 'border-emphasis' : 'border'}
                                    borderRadius="200"
                                >
                                    <InlineStack align="space-between" blockAlign="center" gap="300">
                                        <BlockStack gap="050">
                                            <Text as="span" variant="bodyMd" fontWeight="bold">
                                                {group.title || `Slot ${index + 1}`}
                                            </Text>
                                            <Text as="span" variant="bodySm" tone="subdued">
                                                {group.baseShopifyCollectionTitle || 'No base collection'}
                                            </Text>
                                        </BlockStack>
                                        <Button onClick={() => setSelectedGroupIndex(index)}>Edit</Button>
                                    </InlineStack>
                                </Box>
                            ))}
                        </BlockStack>
                    </Box>
                </Card>
            </Layout.Section>

            <Layout.Section>
                <Card>
                    <Box padding="500">
                        {activeGroup && activeGroupIndex !== null ? (
                            <BlockStack gap="500">
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h2" variant="headingMd">Edit package slot</Text>
                                    <Button tone="critical" onClick={() => removeResultGroup(activeGroupIndex)}>Remove</Button>
                                </InlineStack>

                                <FormLayout>
                                    <TextField
                                        label="Title"
                                        value={activeGroup.title}
                                        onChange={(value) => handleGroupTitleChange(activeGroupIndex, value)}
                                        autoComplete="off"
                                    />
                                    <Select
                                        label="Base collection"
                                        options={collectionOptions}
                                        value={activeGroup.baseShopifyCollectionId ? String(activeGroup.baseShopifyCollectionId) : ''}
                                        onChange={(value) => {
                                            const collection = findCollection(value ? Number(value) : null);
                                            patchResultGroup(activeGroupIndex, {
                                                baseShopifyCollectionId: collection?.id ?? null,
                                                baseShopifyCollectionTitle: collection?.title ?? '',
                                                baseShopifyCollectionHandle: collection?.handle ?? '',
                                            });
                                        }}
                                    />
                                    <Checkbox
                                        label="Required"
                                        checked={activeGroup.isRequired}
                                        onChange={(value) => patchResultGroup(activeGroupIndex, { isRequired: value })}
                                    />
                                    <InlineStack gap="400" wrap>
                                        <TextField
                                            label="Minimum selections"
                                            type="number"
                                            value={String(activeGroup.minSelections)}
                                            onChange={(value) => patchResultGroup(activeGroupIndex, { minSelections: parseWholeNumber(value) })}
                                            autoComplete="off"
                                        />
                                        <TextField
                                            label="Maximum selections"
                                            type="number"
                                            value={String(activeGroup.maxSelections)}
                                            onChange={(value) => patchResultGroup(activeGroupIndex, { maxSelections: parseWholeNumber(value) })}
                                            autoComplete="off"
                                        />
                                    </InlineStack>
                                </FormLayout>

                                <Box padding="400" borderWidth="025" borderColor="border" borderRadius="200">
                                    <BlockStack gap="400">
                                        <Text as="h3" variant="headingSm">Relevant answer filters</Text>

                                        {answerFilterCollections.length === 0 ? (
                                            <Text as="p" tone="subdued">No answer filters have been added.</Text>
                                        ) : (
                                            <InlineStack gap="400" wrap>
                                                {answerFilterCollections.map((collection) => (
                                                    <Checkbox
                                                        key={collection.id}
                                                        label={collection.title}
                                                        checked={activeGroup.filterScopes.some((scope) => scope.scopeType === 'ShopifyCollection' && scope.shopifyCollectionId === collection.id)}
                                                        onChange={(checked) => toggleCollectionScope(activeGroupIndex, collection, checked)}
                                                    />
                                                ))}
                                            </InlineStack>
                                        )}

                                        <FormLayout>
                                            <Select
                                                label="No-results behavior"
                                                options={noResultsBehaviorOptions}
                                                value={activeGroup.noResultsBehavior}
                                                onChange={(value) => patchResultGroup(activeGroupIndex, { noResultsBehavior: value as ProductGuideResultGroup['noResultsBehavior'] })}
                                            />
                                            {activeGroup.noResultsBehavior === 'UseFallbackCollection' && (
                                                <Select
                                                    label="Fallback collection"
                                                    options={collectionOptions}
                                                    value={activeGroup.fallbackShopifyCollectionId ? String(activeGroup.fallbackShopifyCollectionId) : ''}
                                                    onChange={(value) => {
                                                        const collection = findCollection(value ? Number(value) : null);
                                                        patchResultGroup(activeGroupIndex, {
                                                            fallbackShopifyCollectionId: collection?.id ?? null,
                                                            fallbackShopifyCollectionTitle: collection?.title ?? '',
                                                            fallbackShopifyCollectionHandle: collection?.handle ?? '',
                                                        });
                                                    }}
                                                />
                                            )}
                                        </FormLayout>
                                    </BlockStack>
                                </Box>

                                <Box padding="400" borderWidth="025" borderColor="border" borderRadius="200">
                                    <BlockStack gap="300">
                                        <Text as="h3" variant="headingSm">Manual products</Text>
                                        <InlineStack gap="300" blockAlign="end" wrap={false}>
                                            <Box width="100%">
                                                <TextField
                                                    label="Search products"
                                                    value={productSearchTerms[activeGroupIndex] ?? ''}
                                                    onChange={(value) => setProductSearchTerms((current) => ({ ...current, [activeGroupIndex]: value }))}
                                                    autoComplete="off"
                                                />
                                            </Box>
                                            <Button loading={!!productSearchLoading[activeGroupIndex]} onClick={() => searchProducts(activeGroupIndex)}>
                                                Search
                                            </Button>
                                        </InlineStack>

                                        {(productSearchResults[activeGroupIndex] ?? []).map((product) => (
                                            <InlineStack key={product.id} align="space-between" blockAlign="center" gap="300">
                                                <BlockStack gap="050">
                                                    <Text as="span" variant="bodyMd" fontWeight="bold">{product.title}</Text>
                                                    <Text as="span" variant="bodySm" tone="subdued">
                                                        {[product.vendor, product.sku, product.status].filter(Boolean).join(' / ')}
                                                    </Text>
                                                </BlockStack>
                                                <Button onClick={() => addSearchedProduct(activeGroupIndex, product)}>Add</Button>
                                            </InlineStack>
                                        ))}
                                        <Box>
                                            <Button onClick={() => addResultProduct(activeGroupIndex)}>Add manual product</Button>
                                        </Box>
                                    </BlockStack>
                                </Box>

                                <BlockStack gap="300">
                                    <InlineStack align="space-between" blockAlign="center">
                                        <Text as="h3" variant="headingSm">Selected products</Text>
                                        <Button onClick={() => setShowSlotAdvanced((current) => !current)}>
                                            {showSlotAdvanced ? 'Hide IDs' : 'Show IDs'}
                                        </Button>
                                    </InlineStack>

                                    {activeGroup.products.length === 0 && (
                                        <Text as="p" tone="subdued">No products selected.</Text>
                                    )}
                                    {activeGroup.products.map((product, productIndex) => (
                                        <Box key={product.id ?? `product-${productIndex}`} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                                            <BlockStack gap="300">
                                                <InlineStack align="space-between" blockAlign="center">
                                                    <BlockStack gap="050">
                                                        <Text as="span" variant="bodyMd" fontWeight="bold">
                                                            {product.productTitle || `Product ${product.shopifyProductId || productIndex + 1}`}
                                                        </Text>
                                                        <Text as="span" variant="bodySm" tone="subdued">
                                                            {[product.variantTitle, product.sku].filter(Boolean).join(' / ') || 'No variant selected'}
                                                        </Text>
                                                    </BlockStack>
                                                    <Button tone="critical" onClick={() => removeResultProduct(activeGroupIndex, productIndex)}>Remove</Button>
                                                </InlineStack>

                                                <InlineStack gap="400" wrap>
                                                    <TextField
                                                        label="Quantity"
                                                        type="number"
                                                        value={String(product.quantity)}
                                                        onChange={(value) => patchResultProduct(activeGroupIndex, productIndex, { quantity: parseWholeNumber(value) || 1 })}
                                                        autoComplete="off"
                                                    />
                                                    <Checkbox
                                                        label="Default selected"
                                                        checked={product.isDefaultSelected}
                                                        onChange={(value) => patchResultProduct(activeGroupIndex, productIndex, { isDefaultSelected: value })}
                                                    />
                                                </InlineStack>

                                                {showSlotAdvanced && (
                                                    <FormLayout>
                                                        <TextField
                                                            label="Shopify product ID"
                                                            type="number"
                                                            value={numberValue(product.shopifyProductId)}
                                                            onChange={(value) => patchResultProduct(activeGroupIndex, productIndex, { shopifyProductId: parseWholeNumber(value) })}
                                                            autoComplete="off"
                                                        />
                                                        <TextField
                                                            label="Shopify variant ID"
                                                            type="number"
                                                            value={numberValue(product.shopifyProductVariantId)}
                                                            onChange={(value) => patchResultProduct(activeGroupIndex, productIndex, { shopifyProductVariantId: parseNullableWholeNumber(value) })}
                                                            autoComplete="off"
                                                        />
                                                    </FormLayout>
                                                )}
                                            </BlockStack>
                                        </Box>
                                    ))}
                                </BlockStack>

                                {showSlotAdvanced && (
                                    <FormLayout>
                                        <TextField
                                            label="Slot key"
                                            value={activeGroup.slotKey}
                                            onChange={(value) => patchResultGroup(activeGroupIndex, { slotKey: value })}
                                            autoComplete="off"
                                        />
                                        <TextField
                                            label="Description"
                                            value={activeGroup.description}
                                            onChange={(value) => patchResultGroup(activeGroupIndex, { description: value })}
                                            autoComplete="off"
                                        />
                                    </FormLayout>
                                )}
                            </BlockStack>
                        ) : (
                            <Text as="p" tone="subdued">Select or add a package slot.</Text>
                        )}
                    </Box>
                </Card>
            </Layout.Section>
        </Layout>
    );

    const previewTabMarkup = (
        <Layout>
            <Layout.Section>
                <Card>
                    <Box padding="500">
                        <BlockStack gap="500">
                            <BlockStack gap="100">
                                <Text as="h2" variant="headingMd">{guide.name || 'Product guide'}</Text>
                                {guide.description && <Text as="p" tone="subdued">{guide.description}</Text>}
                            </BlockStack>

                            <BlockStack gap="300">
                                <Text as="h3" variant="headingSm">Questions</Text>
                                {guide.questions.length === 0 ? (
                                    <Text as="p" tone="subdued">No shopper questions configured.</Text>
                                ) : guide.questions.map((question, index) => (
                                    <Box key={question.id ?? `preview-question-${index}`} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                                        <BlockStack gap="200">
                                            <Text as="p" fontWeight="bold">{question.prompt || `Question ${index + 1}`}</Text>
                                            <InlineStack gap="200" wrap>
                                                {question.answers.length === 0 ? (
                                                    <Text as="span" tone="subdued">No answers</Text>
                                                ) : question.answers.map((answer, answerIndex) => (
                                                    <Button key={answer.id ?? `preview-answer-${answerIndex}`} disabled>
                                                        {answer.label || `Answer ${answerIndex + 1}`}
                                                    </Button>
                                                ))}
                                            </InlineStack>
                                        </BlockStack>
                                    </Box>
                                ))}
                            </BlockStack>

                            <BlockStack gap="300">
                                <Text as="h3" variant="headingSm">Package slots</Text>
                                {guide.resultGroups.length === 0 ? (
                                    <Text as="p" tone="subdued">No package slots configured.</Text>
                                ) : guide.resultGroups.map((group, index) => (
                                    <Box key={group.id ?? `preview-slot-${index}`} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                                        <InlineStack align="space-between" blockAlign="center">
                                            <BlockStack gap="050">
                                                <Text as="p" fontWeight="bold">{group.title || `Slot ${index + 1}`}</Text>
                                                <Text as="p" tone="subdued">
                                                    {group.baseShopifyCollectionTitle || 'No base collection'}
                                                </Text>
                                            </BlockStack>
                                            <Button disabled>Select</Button>
                                        </InlineStack>
                                    </Box>
                                ))}
                            </BlockStack>
                        </BlockStack>
                    </Box>
                </Card>
            </Layout.Section>
        </Layout>
    );

    return (
        <Page
            title={guide.id ? guide.name : 'Create Product Guide'}
            backAction={{ content: 'Product guides', onAction: () => navigate('/product-guides') }}
            primaryAction={{ content: 'Save', loading: loadingSave, onAction: handleSave }}
        >
            <BlockStack gap="500">
                {setupDataError && (
                    <Banner tone="warning">
                        <p>{setupDataError}</p>
                    </Banner>
                )}

                {saveError && (
                    <Banner tone="critical">
                        <p>{saveError}</p>
                    </Banner>
                )}

                <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                    {selectedTab === 0 && setupTabMarkup}
                    {selectedTab === 1 && questionsTabMarkup}
                    {selectedTab === 2 && slotsTabMarkup}
                    {selectedTab === 3 && previewTabMarkup}
                </Tabs>

                <PageActions
                    primaryAction={{ content: 'Save', loading: loadingSave, onAction: handleSave }}
                    secondaryActions={[
                        { content: 'Cancel', onAction: () => navigate('/product-guides') },
                        ...(guide.id ? [{ content: 'Delete', destructive: true, onAction: handleDelete }] : []),
                    ]}
                />
            </BlockStack>
        </Page>
    );
};

export default observer(ProductGuideEditorPage);
